import { getBackendUrl } from '@/lib/backendUrl';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CalendarClock, MapPin, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import QboSandboxSettings from './QboSandboxSettings';

const API = getBackendUrl();
const UNRESOLVED = '__unresolved__';

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Admin only — these settings change what every future draft/export
// computes (invoice Due Date and the Region 1/2 Location on the export),
// plus the Preview-only QBO Sandbox connection used by direct QBO create.
export default function SettingsPanel() {
  return (
    <div className="space-y-8" data-testid="sales-invoicing-settings-panel">
      <DueDaysSettings />
      <AuditorRegionSettings />
      <QboSandboxSettings />
    </div>
  );
}

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function DueDaysSettings() {
  const [orgDefault, setOrgDefault] = useState('');
  const [savingDefault, setSavingDefault] = useState(false);
  const [sites, setSites] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, sitesRes] = await Promise.all([
        axios.get(`${API}/api/settings`, { headers: authHeaders() }),
        axios.get(`${API}/api/sales-invoicing/settings/due-days-overrides`, { headers: authHeaders(), params: debouncedSearch ? { search: debouncedSearch } : {} }),
      ]);
      setOrgDefault(String(settingsRes.data.sales_invoicing_default_due_days ?? 30));
      setSites(sitesRes.data.sites || []);
      setTotalCount(sitesRes.data.total_count ?? (sitesRes.data.sites || []).length);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load due-days settings.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const saveOrgDefault = async () => {
    const days = parseInt(orgDefault, 10);
    if (Number.isNaN(days) || days < 0 || days > 365) {
      toast.error('Enter a whole number of days between 0 and 365.');
      return;
    }
    setSavingDefault(true);
    try {
      await axios.put(`${API}/api/settings`, { sales_invoicing_default_due_days: days }, { headers: authHeaders() });
      toast.success('Default invoice due days saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save.');
    } finally {
      setSavingDefault(false);
    }
  };

  const saveOverride = async (siteId, value) => {
    const days = value === '' ? null : parseInt(value, 10);
    if (days !== null && (Number.isNaN(days) || days < 0 || days > 365)) {
      toast.error('Enter a whole number of days between 0 and 365, or clear it.');
      return;
    }
    try {
      await axios.put(`${API}/api/sales-invoicing/sites/${siteId}/due-days-override`, { days }, { headers: authHeaders() });
      setSites((prev) => prev.map((s) => (s.site_id === siteId ? { ...s, due_days_override: days } : s)));
      toast.success('Saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save.');
    }
  };

  return (
    <div data-testid="due-days-settings">
      <div className="flex items-center gap-2 mb-3">
        <CalendarClock className="h-5 w-5 text-rose-600" />
        <h2 className="text-lg font-semibold text-slate-900">Invoice due days</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        The export's Due Date column always uses a site's own override if one is set, else this organisation-wide default — never a hard-coded assumption.
      </p>

      <div className="flex items-end gap-3 mb-6">
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Organisation default (calendar days)</label>
          <Input
            type="number" min={0} max={365} value={orgDefault}
            onChange={(e) => setOrgDefault(e.target.value)}
            className="w-32" data-testid="due-days-org-default-input"
          />
        </div>
        <Button onClick={saveOrgDefault} disabled={savingDefault} data-testid="due-days-org-default-save-btn" className="min-w-[120px]">
          {savingDefault ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save default'}
        </Button>
      </div>

      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium text-slate-600">Per-site overrides</label>
        <Input
          placeholder="Search sites…" value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-56" data-testid="due-days-site-search-input"
        />
      </div>
      {!search && totalCount > sites.length && (
        <p className="text-xs text-slate-400 mb-2" data-testid="due-days-site-truncated-hint">
          Showing {sites.length} of {totalCount} sites (sites with an override first) — search by name to find another.
        </p>
      )}
      <div className="overflow-x-auto border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Site</TableHead>
              <TableHead className="w-64">Override (days)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={2} className="text-center text-slate-500">Loading…</TableCell></TableRow>}
            {!loading && sites.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-slate-500">No sites found.</TableCell></TableRow>}
            {sites.map((s) => (
              <TableRow key={s.site_id} data-testid={`due-days-site-row-${s.site_id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>
                  <DueDaysOverrideInput site={s} onSave={saveOverride} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function DueDaysOverrideInput({ site, onSave }) {
  const [value, setValue] = useState(site.due_days_override == null ? '' : String(site.due_days_override));
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Input
        type="number" min={0} max={365} placeholder="Use default" value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-28 min-w-0" data-testid={`due-days-override-input-${site.site_id}`}
      />
      <Button size="sm" variant="outline" onClick={() => onSave(site.site_id, value)} data-testid={`due-days-override-save-${site.site_id}`}>Save</Button>
      {value !== '' && (
        <Button size="sm" variant="ghost" onClick={() => { setValue(''); onSave(site.site_id, ''); }} data-testid={`due-days-override-clear-${site.site_id}`}>Clear</Button>
      )}
    </div>
  );
}

const REGION_OPTIONS = [
  { value: 'region_1', label: 'Region 1' },
  { value: 'region_2', label: 'Region 2' },
];

function AuditorRegionSettings() {
  const [auditors, setAuditors] = useState([]);
  const [allAuditors, setAllAuditors] = useState([]);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/sales-invoicing/settings/auditor-regions`, { headers: authHeaders() });
      setAllAuditors(res.data.auditors || []);
    } catch {
      // unresolved-count notice simply won't update; the table below still loads independently
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/sales-invoicing/settings/auditor-regions`, { headers: authHeaders(), params: debouncedSearch ? { search: debouncedSearch } : {} });
      setAuditors(res.data.auditors || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load auditor regions.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { load(); }, [load]);

  const saveRegion = async (auditorId, region) => {
    try {
      await axios.put(`${API}/api/sales-invoicing/auditors/${auditorId}/qbo-region`, { region }, { headers: authHeaders() });
      setAuditors((prev) => prev.map((a) => (a.auditor_id === auditorId ? { ...a, qbo_billing_region: region } : a)));
      setAllAuditors((prev) => prev.map((a) => (a.auditor_id === auditorId ? { ...a, qbo_billing_region: region } : a)));
      toast.success('Saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save.');
    }
  };

  const unresolvedCount = allAuditors.filter((a) => a.is_active && !a.qbo_billing_region).length;

  return (
    <div data-testid="auditor-region-settings">
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="h-5 w-5 text-rose-600" />
        <h2 className="text-lg font-semibold text-slate-900">Auditor → QBO region</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        A joint job uses Region 2 if any assigned auditor is set to Region 2 here, else Region 1 — the rule every past export has already used.
        A new auditor has no region until assigned here; any job that includes them stays blocked ("Decision needed") until you assign one.
      </p>
      {unresolvedCount > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-sm px-3 py-2 mb-3" data-testid="auditor-region-unresolved-notice">
          {unresolvedCount} active auditor(s) still have no region assigned.
        </div>
      )}
      <Input
        placeholder="Search auditors…" value={search} onChange={(e) => setSearch(e.target.value)}
        className="w-56 mb-2" data-testid="auditor-region-search-input"
      />
      <div className="overflow-x-auto border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Auditor</TableHead>
              <TableHead className="w-56">QBO Region</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={2} className="text-center text-slate-500">Loading…</TableCell></TableRow>}
            {!loading && auditors.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-slate-500">No auditors found.</TableCell></TableRow>}
            {auditors.map((a) => (
              <TableRow key={a.auditor_id} data-testid={`auditor-region-row-${a.auditor_id}`}>
                <TableCell className="font-medium">
                  {a.name}{!a.is_active && <Badge variant="secondary" className="ml-2">Inactive</Badge>}
                </TableCell>
                <TableCell>
                  <Select
                    value={a.qbo_billing_region || UNRESOLVED}
                    onValueChange={(v) => saveRegion(a.auditor_id, v === UNRESOLVED ? null : v)}
                  >
                    <SelectTrigger className="w-44" data-testid={`auditor-region-select-${a.auditor_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNRESOLVED}>— Unresolved —</SelectItem>
                      {REGION_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
